import { useMemo } from 'react'
import type { Habit, HabitLog } from '@/src/renderer/lib/db/models'
import { completedKeySet } from '../../hooks/useHabitLogs'
import { dateKey, daysBetween, startOfDay } from '../../lib/date'
import { habitDayState } from '../../lib/habitDay'
import { habitTimeSlot, TIME_SLOT_ORDER } from '../../lib/habitsContent'
import { computeStreak } from '../../lib/streak'
import { HabitRow } from '../HabitRow'
import './HabitList.scss'

interface HabitListProps {
  habits: Habit[]
  logsByHabit: Map<string, HabitLog[]>
  selectedDate: Date
  today: Date
  graceDays: number
  onToggle: (habit: Habit, dayKey: string, next: boolean) => void
}

/**
 * Flat list for the selected day: open habits first (sorted by time-of-day,
 * then sortOrder), completed/satisfied habits sink below a divider (D10b/c).
 * Streaks are computed as-of-today regardless of the selected day.
 */
export function HabitList({ habits, logsByHabit, selectedDate, today, graceDays, onToggle }: HabitListProps) {
  const isFuture = daysBetween(startOfDay(today), startOfDay(selectedDate)) > 0
  const selKey = dateKey(selectedDate)

  const { open, done } = useMemo(() => {
    const entries = habits.flatMap((habit) => {
      if (habit.pausedAt) return []
      const keys = completedKeySet(logsByHabit.get(habit.id))
      const dayState = habitDayState(habit, keys, selectedDate)
      if (!dayState.appears) return []
      const streak = computeStreak(habit, keys, today, graceDays)
      return [{ habit, dayState, streak }]
    })
    const bySlot = (a: typeof entries[number], b: typeof entries[number]) =>
      TIME_SLOT_ORDER[habitTimeSlot(a.habit)] - TIME_SLOT_ORDER[habitTimeSlot(b.habit)] ||
      a.habit.sortOrder - b.habit.sortOrder
    return {
      open: entries.filter(e => !e.dayState.done).sort(bySlot),
      done: entries.filter(e => e.dayState.done).sort(bySlot),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habits, logsByHabit, selKey, today, graceDays])

  const render = (e: { habit: Habit; dayState: ReturnType<typeof habitDayState>; streak: ReturnType<typeof computeStreak> }) => (
    <HabitRow
      key={e.habit.id}
      habit={e.habit}
      dayState={e.dayState}
      streak={e.streak}
      disabled={isFuture}
      onToggle={() => onToggle(e.habit, selKey, !e.dayState.completed)}
    />
  )

  return (
    <div className="habit-list">
      {open.map(render)}
      {done.length > 0 && open.length > 0 && <div className="habit-list__divider" />}
      {done.map(render)}
    </div>
  )
}
